/**
 * Что извлекаем, а что грузим как есть.
 *
 * Время извлечения зависит от ДЛИТЕЛЬНОСТИ (конвейер идёт на фиксированной
 * скорости, см. MIN_EXTRACT_BITRATE_BPS ниже), а экономия трафика — от
 * РАЗМЕРА. Это разные величины, и голый размер их не связывает: замеры с
 * прода 11.08 показали файл на 0,33 Мбит/с, где извлечение окупается только
 * при канале медленнее ~30 Мбит/с, и файл на 2,6 Мбит/с, который окупается
 * почти всегда — при сопоставимом размере. Поэтому итоговое решение —
 * shouldExtract() c известной длительностью, по битрейту.
 *
 * MIN_EXTRACT_BYTES остаётся, но в двух более узких ролях:
 *  - дешёвый предфильтр СНАРУЖИ конвейера (index.js, до подъёма воркера) —
 *    длительности там ещё нет, а поднимать воркер ради заведомо мелкого
 *    файла незачем;
 *  - запасной критерий ВНУТРИ конвейера для записей MediaRecorder (запись
 *    экрана и встреч — основная часть нашего видео): их контейнер не несёт
 *    длительности (adapter.openInput() → durationFromMetadata === null),
 *    посчитать битрейт не из чего.
 * 64 МиБ — осознанно консервативная заглушка для этого запасного пути;
 * настоящее число берётся из телеметрии после раскатки (спека §5).
 */
export const MIN_EXTRACT_BYTES = 64 * 1024 * 1024;

/**
 * Порог по битрейту: извлекаем, если размер / длительность выше него.
 *
 * Откуда 0,2 Мбит/с — теперь из замеров, а не из предположения.
 *
 * Извлечение окупается там, где битрейт видео выше отношения
 * (скорость канала / скорость конвейера). Конвейер идёт на ≈105× реального
 * времени — прод 11.08, стабильно на всех файлах.
 *
 * Скорость канала взята из телеметрии `upload_speed`, 38 замеров с прода
 * 11.08: медиана 19,9 Мбит/с, четверти 9,3 и 48,0, крайние 1,1 и 308,8.
 * При медиане точка безубыточности выходит 19,9 / 105 ≈ 0,19 Мбит/с.
 *
 * Первая версия порога стояла на 0,5 — я исходил из офисных 50 Мбит/с,
 * которых на деле нет ни у медианы, ни у трёх четвертей пользователей.
 * Порог оказался втрое строже нужного и пропускал файлы, где извлечение
 * выгодно.
 *
 * Ошибаться вниз здесь дешевле, чем вверх: лишнее извлечение стоит секунд
 * процессорного времени, пропущенное — десятков секунд ожидания и всего
 * трафика. Поэтому берём медианную точку, а не консервативную по верхней
 * четверти.
 */
export const MIN_EXTRACT_BITRATE_BPS = 0.2 * 1e6;

const VIDEO_EXT = /\.(mp4|m4v|mov|webm|mkv)$/i;

export function isVideo(file) {
  if (!file) return false;
  if (typeof file.type === 'string') {
    if (file.type.startsWith('video/')) return true;
    // Аудиозапись из браузера (MediaRecorder) часто называется rec.webm при
    // type: 'audio/webm' — расширение делят с видео, поэтому MIME решает
    // первым и в обе стороны, иначе такой файл едет на лишнее Opus→Opus
    // перекодирование.
    if (file.type.startsWith('audio/')) return false;
  }
  return VIDEO_EXT.test(file.name || '');
}

/**
 * durationSec — длительность из заголовков контейнера (adapter.openInput().
 * durationFromMetadata), если она уже известна на момент вызова:
 *  - не передана вовсе (index.js, дешёвая проверка ДО подъёма воркера — там
 *    длительности взяться неоткуда) или null/0 (MediaRecorder — контейнер
 *    длительности не несёт) → решаем по размеру, как раньше;
 *  - известна и больше нуля (pipeline.js, после adapter.openInput()) →
 *    решаем по битрейту.
 * `0` намеренно уходит в ветку "по размеру" — иначе размер/0 даст Infinity
 * или NaN вместо честного решения.
 */
export function shouldExtract(file, durationSec, {
  minBytes = MIN_EXTRACT_BYTES,
  minBitrateBps = MIN_EXTRACT_BITRATE_BPS,
} = {}) {
  if (!isVideo(file)) return false;
  if (durationSec) {
    return ((file.size || 0) * 8) / durationSec >= minBitrateBps;
  }
  return (file.size || 0) >= minBytes;
}
